import { CANCER_TYPE_PARAM_ID } from '@/config/constants';
import logger from '@/layers/support/monitoring/logger';
import { formatParamOptionLabel } from '@/layers/support/utils/formatParamOptionLabel';
import { FitStatus } from '@/types/modelEnums';
import { PatientExtended, PatientParam, PatientParamValues, PatientProfile } from '@/types/models';
import {
  NewPatientByUserIdRequest,
  NewPatientRequest,
  PatientParamValuesExtended,
} from '@/types/patients';
import { fetchWrapper } from '../utils/fetchWrapper';

const LOG_MODULE = 'FE [Presentation / Services / Patients]';

const API_URL = '/api/users/patients';

const ENABLE_CLIENT_CACHE = process.env.NODE_ENV !== 'development';

type CacheEntry<T> = {
  value: T;
  expiresAtMs: number;
};

const PATIENT_PARAMS_DICTIONARY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
let patientParamsDictionaryCache: CacheEntry<{ patientParamsDictionary: PatientParam[] }> | null =
  null;
let patientParamsDictionaryInFlight: Promise<{ patientParamsDictionary: PatientParam[] }> | null =
  null;

function nowMs() {
  return Date.now();
}

function getFreshCacheValue<T>(entry: CacheEntry<T> | null) {
  if (!entry) return null;
  if (entry.expiresAtMs <= nowMs()) return null;
  return entry.value;
}

export const fetchPatients = async (filters: Record<string, any[]>): Promise<PatientExtended[]> => {
  logger.debug(`${LOG_MODULE} fetchPatients: begin`, { filters });

  try {
    const result = await fetchWrapper.get<PatientExtended[]>(
      `${API_URL}?filters=${JSON.stringify(filters)}`,
    );

    logger.debug(`${LOG_MODULE} fetchPatients: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchPatients`, { error });
    throw error;
  }
};

export const fetchPatientById = async (id: string): Promise<PatientProfile> => {
  logger.debug(`${LOG_MODULE} fetchPatientById: begin`, { id });

  try {
    const result = await fetchWrapper.get<PatientProfile>(`${API_URL}/${id}`);

    logger.debug(`${LOG_MODULE} fetchPatientById: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchPatientById`, { error });
    throw error;
  }
};

export const fetchPatientByUserId = async (id: string): Promise<PatientProfile> => {
  logger.debug(`${LOG_MODULE} fetchPatientByUserId: begin`, { id });

  try {
    const result = await fetchWrapper.get<PatientProfile>(`${API_URL}/user/${id}`);

    logger.debug(`${LOG_MODULE} fetchPatientByUserId: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchPatientByUserId`, { error });
    throw error;
  }
};

export const createPatient = async (patient: NewPatientRequest): Promise<PatientProfile> => {
  logger.debug(`${LOG_MODULE} createPatient: begin`, { patient });

  try {
    const result = await fetchWrapper.post<PatientProfile>(API_URL, patient);

    logger.debug(`${LOG_MODULE} createPatient: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} createPatient`, { error });
    throw error;
  }
};

export const createPatientByUserId = async (
  patient: NewPatientByUserIdRequest,
): Promise<PatientProfile> => {
  logger.debug(`${LOG_MODULE} createPatientByUserId: begin`, { patient });

  try {
    const result = await fetchWrapper.post<PatientProfile>(
      `${API_URL}/user/${patient.user_id}`,
      patient,
    );

    logger.debug(`${LOG_MODULE} createPatientByUserId: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} createPatientByUserId`, { error });
    throw error;
  }
};

export const updatePatient = async (
  id: string,
  patient: Partial<PatientProfile>,
): Promise<PatientProfile> => {
  logger.debug(`${LOG_MODULE} updatePatient: begin`, { id, patient });

  try {
    const result = await fetchWrapper.put<PatientProfile>(`${API_URL}/${id}`, patient);

    logger.debug(`${LOG_MODULE} updatePatient: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} updatePatient`, { error });
    throw error;
  }
};

export const deletePatient = async (id: string): Promise<void> => {
  logger.debug(`${LOG_MODULE} deletePatient: begin`, { id });

  try {
    const result = await fetchWrapper.delete<void>(`${API_URL}/${id}`);

    logger.debug(`${LOG_MODULE} deletePatient: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} deletePatient`, { error });
    throw error;
  }
};

export const fetchpatientParamsDictionary = async (): Promise<{
  patientParamsDictionary: PatientParam[];
}> => {
  logger.debug(`${LOG_MODULE} fetchpatientParamsDictionary: begin`);

  try {
    if (ENABLE_CLIENT_CACHE) {
      const cached = getFreshCacheValue(patientParamsDictionaryCache);
      if (cached) {
        logger.debug(`${LOG_MODULE} fetchpatientParamsDictionary: cache hit`, {
          count: cached.patientParamsDictionary.length,
        });
        return cached;
      }
    }

    if (patientParamsDictionaryInFlight) {
      logger.debug(`${LOG_MODULE} fetchpatientParamsDictionary: dedupe in-flight`);
      return await patientParamsDictionaryInFlight;
    }

    patientParamsDictionaryInFlight = fetchWrapper
      .get<{ patientParamsDictionary: PatientParam[] }>(`${API_URL}/params`)
      .then(result => {
        if (ENABLE_CLIENT_CACHE) {
          patientParamsDictionaryCache = {
            value: result,
            expiresAtMs: nowMs() + PATIENT_PARAMS_DICTIONARY_TTL_MS,
          };
        }
        return result;
      })
      .finally(() => {
        patientParamsDictionaryInFlight = null;
      });

    const result = await patientParamsDictionaryInFlight;

    logger.debug(`${LOG_MODULE} fetchpatientParamsDictionary: end`);
    return result;
  } catch (error) {
    patientParamsDictionaryInFlight = null;
    logger.error(`${LOG_MODULE} fetchpatientParamsDictionary`, { error });
    throw error;
  }
};

export const getPatientParamBySlug = (params: PatientParam[], slug: string): PatientParam => {
  logger.debug(`${LOG_MODULE} getPatientParamBySlug: begin`, { params, slug });

  try {
    const result = params.find(param => param.slug === slug) as PatientParam;

    logger.debug(`${LOG_MODULE} getPatientParamBySlug: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} getPatientParamBySlug`, { error });
    throw error;
  }
};

export const getPatientParamValuesExtended = (
  patientParamsDictionary: PatientParam[],
  selectedOptions: PatientParamValues,
): PatientParamValuesExtended => {
  logger.debug(`${LOG_MODULE} getPatientParamValuesExtended: begin`, {
    selectedOptions,
  });

  const result: PatientParamValuesExtended = {};

  try {
    selectedOptions &&
      Object.keys(selectedOptions).forEach(paramId => {
        const param = patientParamsDictionary.find(p => Number(p.id) === Number(paramId));
        if (param && Array.isArray(selectedOptions[paramId])) {
          const values = selectedOptions[paramId].map(optionId => {
            const option = param.options.find(o => Number(o.id) === Number(optionId));
            return {
              value: optionId,
              // Centralized label formatting (e.g. strip the leading zero from
              // zero-padded age options) so all read-only views render "8" not "08".
              label: option ? formatParamOptionLabel(param.slug, option.option) : '',
            };
          });

          result[param.id] = {
            param,
            values,
          };
        }
      });

    logger.debug(`${LOG_MODULE} getPatientParamValuesExtended: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} getPatientParamValuesExtended`, { error });
    throw error;
  }
};

export const updatePatientParamValues = async (
  id: string,
  params: PatientParamValues,
): Promise<PatientProfile> => {
  logger.debug(`${LOG_MODULE} updatePatientParamValues: begin`, { id, params });

  try {
    const result = await fetchWrapper.put<PatientProfile>(`${API_URL}/${id}/params`, params);

    logger.debug(`${LOG_MODULE} updatePatientParamValues: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} updatePatientParamValues`, { error });
    throw error;
  }
};

type Params = PatientParamValues;

export function doesPatientFitProgram(patientParams: Params, programParams: Params): FitStatus {
  logger.debug(`${LOG_MODULE} doesPatientFitProgram: begin`, {
    patientParams,
    programParams,
  });

  const cancerTypeIdStr = CANCER_TYPE_PARAM_ID.toString();
  const programCancerTypes = programParams[cancerTypeIdStr] || [];
  const patientCancerTypes = patientParams[cancerTypeIdStr] || [];

  logger.debug(`${LOG_MODULE} doesPatientFitProgram: Checking cancer types`, {
    cancerTypeId: cancerTypeIdStr,
    programCancerTypes,
    patientCancerTypes,
  });

  if (
    !Array.isArray(programCancerTypes) ||
    programCancerTypes.length === 0 ||
    !Array.isArray(patientCancerTypes) ||
    patientCancerTypes.length === 0
  ) {
    logger.debug(
      `${LOG_MODULE} doesPatientFitProgram: No cancer types specified or invalid format`,
      {
        programCancerTypes,
        patientCancerTypes,
      },
    );
    return FitStatus.NOT_FIT;
  }

  const hasMatchingCancerType = programCancerTypes.some(programOption =>
    patientCancerTypes.includes(programOption),
  );

  const fitStatus = hasMatchingCancerType ? FitStatus.FULLY_FIT : FitStatus.NOT_FIT;

  logger.debug(`${LOG_MODULE} doesPatientFitProgram: end`, {
    hasMatchingCancerType,
    fitStatus,
  });

  return fitStatus;
}
